/**
 * 見積書エディタ (CreateQuoteDialog) の状態の型と、画面から切り離した計算・入力変換。
 */
import type {
  BillingAddress,
  DeliveryAddress,
  LineDiscountType,
  QuoteInstallments,
  QuotePaymentMethod,
  QuoteTradeInType,
} from '@goenhub/shared';
import { calcLineAmounts } from '@goenhub/shared';
import type { KeyboardEvent } from 'react';

/**
 * 消費税率。functions/src/quotes/_helpers.ts の TAX_RATE と同じ値。
 * 製品リストにない品目 (手入力) の税込単価の計算に使う。
 */
export const TAX_RATE = 0.1;

/** 納品先「請求先住所と同じ」(= 書類に納品先を出さない)。createQuote / updateQuoteDraft の約束値。 */
export const DELIVERY_SAME_AS_BILLING = '__NONE__';

export interface EditorLine {
  key: string;
  /**
   * 製品リスト (カタログ) の品目か。true のとき単価はカタログの価格で固定し、手入力させない。
   * 既存の見積行は SKU があればカタログ品目として扱う。
   */
  fromCatalog: boolean;
  catalogId: string | null;
  itemId: string | null;
  name: string;
  /** 型番。製品リストにない品目は空文字 */
  modelName: string;
  variation: string | null;
  sku: string | null;
  /** 価格表の「製品カテゴリ」(Module 等)。表示とセール期の注意にだけ使う */
  productCategory: string | null;
  priceExcludingTax: number | null;
  priceIncludingTax: number | null;
  /** null = 未入力 */
  quantity: number | null;
  discountType: LineDiscountType;
  /** 値引きの入力値 (空 = 割引なし) */
  discountValue: string;
  /** 「＋ 割引」で割引欄を開いた状態 (値が空でも欄を出す) */
  discountOpen: boolean;
}

export interface EditorState {
  title: string;
  /** YYYY-MM-DD。'' = 未入力 (新規作成時はサーバーが自動で決める) */
  issued: string;
  valid: string;
  lines: EditorLine[];
  paymentMethod: QuotePaymentMethod;
  installments: QuoteInstallments;
  hasTradeIn: boolean;
  tradeInType: QuoteTradeInType;
  tradeInAmount: number | null;
  remarks: string;
  hideDiscounts: boolean;
  /** 品目名の下の型番を書類に出さない */
  hideModelNames: boolean;
  /** null = 顧客の既定 */
  billingId: string | null;
  /** null = 顧客の既定 / DELIVERY_SAME_AS_BILLING = 請求先住所と同じ */
  deliveryId: string | null;
  /**
   * 住所を選び直したか。下書きの編集では、選び直すまで見積書に保存済みの住所 (スナップショット) を
   * そのまま使い、保存時も住所 ID を送らない。
   */
  billingTouched: boolean;
  deliveryTouched: boolean;
}

export type AddressKind = 'billing' | 'delivery';
export type AddressEntry = BillingAddress | DeliveryAddress;

let lineSeq = 0;
export function newLineKey(): string {
  lineSeq += 1;
  return `line-${Date.now()}-${lineSeq}`;
}

export function emptyLine(): EditorLine {
  return {
    key: newLineKey(),
    fromCatalog: false,
    catalogId: null,
    itemId: null,
    name: '',
    modelName: '',
    variation: null,
    sku: null,
    productCategory: null,
    priceExcludingTax: null,
    priceIncludingTax: null,
    quantity: null,
    discountType: 'amount',
    discountValue: '',
    discountOpen: false,
  };
}

/** 全角の数字・記号を半角にそろえる (日本語入力のまま数字を打った場合に対応)。 */
export function toHalfWidth(s: string): string {
  return s
    .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
    .replace(/／/g, '/')
    .replace(/．/g, '.')
    .replace(/[－−]/g, '-');
}

/** 金額・数量の入力文字列 → 数値。カンマ・¥・円・% を無視する。数値でなければ null。 */
export function parseNumberInput(s: string): number | null {
  const t = toHalfWidth(s).replace(/[,，¥￥\s%％円]/g, '');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** 割引入力文字列 → 数値 (無効 / 0 以下は null = 割引なし)。 */
export function parseDiscountInput(s: string): number | null {
  const n = parseNumberInput(s);
  return n !== null && n > 0 ? n : null;
}

export const formatYen = (n: number) => `¥${Math.round(n).toLocaleString('ja-JP')}`;

/** 品目名・数量・単価がそろった行だけが保存・合計の対象。 */
export function isLineComplete(l: EditorLine): boolean {
  return !!l.name.trim() && l.quantity !== null && l.quantity >= 1 && l.priceExcludingTax !== null;
}

/** 何か入力されているが、そろっていない行。 */
export function isLinePartial(l: EditorLine): boolean {
  return (
    !isLineComplete(l) && (!!l.name.trim() || l.quantity !== null || l.priceExcludingTax !== null)
  );
}

/** 行の割引適用済み金額 (バックエンドの normalizeLineItem と同じ計算)。 */
export function lineAmounts(l: EditorLine) {
  return calcLineAmounts({
    priceExcludingTax: l.priceExcludingTax ?? 0,
    priceIncludingTax: l.priceIncludingTax ?? 0,
    quantity: l.quantity ?? 1,
    discountType: l.discountType,
    discountValue: parseDiscountInput(l.discountValue),
  });
}

export function computeTotals(
  s: Pick<EditorState, 'lines' | 'hasTradeIn' | 'tradeInType' | 'tradeInAmount'>,
) {
  let discountTotal = 0;
  let subtotalExcl = 0;
  let subtotalIncl = 0;
  for (const l of s.lines) {
    if (!isLineComplete(l)) continue;
    const a = lineAmounts(l);
    discountTotal += a.discountAmountExcludingTax;
    subtotalExcl += a.subtotalExcludingTax;
    subtotalIncl += a.subtotalIncludingTax;
  }
  const tradeInDiscount = s.hasTradeIn && s.tradeInType === 'discount' ? (s.tradeInAmount ?? 0) : 0;
  return {
    discountTotal,
    subtotalExcl,
    subtotalIncl,
    tax: subtotalIncl - subtotalExcl,
    tradeInDiscount,
    totalIncl: Math.max(0, subtotalIncl - tradeInDiscount),
  };
}

/** 製品リストにない品目の税込単価。 */
export const includingTax = (excl: number) => Math.round(excl * (1 + TAX_RATE));

// ─── 日付 ───

const pad2 = (n: number) => String(n).padStart(2, '0');

export function isoFromDate(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

export function todayIso(): string {
  return isoFromDate(new Date());
}

export function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  return isoFromDate(new Date(y, m - 1, d + days));
}

/** 'yyyy/mm/dd' (全角・ゼロ埋めなし・'-' 区切りも可) → 'YYYY-MM-DD'。日付として正しくなければ null。 */
export function parseDateInput(s: string): string | null {
  const t = toHalfWidth(s).trim().replace(/[.-]/g, '/');
  const m = t.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;
  return `${y}-${pad2(mo)}-${pad2(d)}`;
}

/** 書類上の表記 (公開見積と同じ 'YYYY/M/D')。 */
export function formatDocDate(iso: string): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return `${y}/${m}/${d}`;
}

// ─── 住所 ───

export function addressLine(
  a: Pick<AddressEntry, 'prefecture' | 'address' | 'buildingName'>,
): string {
  return [a.prefecture, a.address, a.buildingName].filter(Boolean).join('');
}

export function addressSummary(a: AddressEntry): string {
  return [
    a.recipientName ? `宛: ${a.recipientName}` : '',
    [a.postalCode ? `〒${a.postalCode}` : '', a.prefecture, a.address, a.buildingName]
      .filter(Boolean)
      .join(' '),
    a.phone ? `TEL: ${a.phone}` : '',
  ]
    .filter(Boolean)
    .join(' / ');
}

/** 公開見積の isSameAsBilling と同じ判定 (同じなら納品先を書類に出さない)。 */
export function isSameAddress(
  billing: Pick<AddressEntry, 'postalCode' | 'prefecture' | 'address' | 'buildingName'> | null,
  delivery: Pick<AddressEntry, 'postalCode' | 'prefecture' | 'address' | 'buildingName'>,
): boolean {
  return (
    (billing?.postalCode ?? '') === (delivery.postalCode ?? '') &&
    (billing?.prefecture ?? '') === (delivery.prefecture ?? '') &&
    (billing?.address ?? '') === (delivery.address ?? '') &&
    (billing?.buildingName ?? '') === (delivery.buildingName ?? '')
  );
}

/** 顧客の住所一覧を保存できる形にそろえる。空の住所を除き、既定を 1 件にする。 */
export function sanitizeAddressList<T extends AddressEntry>(list: T[]): T[] {
  const trimmed = list
    .map((a) => ({
      ...a,
      label: a.label?.trim() || null,
      recipientName: a.recipientName?.trim() || null,
      postalCode: a.postalCode?.trim() || null,
      prefecture: a.prefecture?.trim() || null,
      address: a.address?.trim() || null,
      buildingName: a.buildingName?.trim() || null,
      phone: a.phone?.trim() || null,
    }))
    .filter(
      (a) =>
        a.label ||
        a.recipientName ||
        a.postalCode ||
        a.prefecture ||
        a.address ||
        a.buildingName ||
        a.phone,
    );
  if (trimmed.length === 0) return [];
  const defaultIdx = trimmed.findIndex((a) => a.isDefault);
  const winner = defaultIdx >= 0 ? defaultIdx : 0;
  return trimmed.map((a, i) => ({ ...a, isDefault: i === winner }));
}

export function newAddressId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `addr-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

// ─── キー入力 ───

/**
 * 日本語の変換を確定する Enter か。これは入力の確定 (保存) に使わない。
 * Safari は compositionend の後に keyCode 229 の keydown を送るため、isComposing だけでは判定できない。
 */
export function isImeEnter(e: KeyboardEvent): boolean {
  return e.nativeEvent.isComposing || e.keyCode === 229;
}
